import { ActionIcon, Anchor, Button, FileButton, Input, Menu, Stack, Text, Tooltip } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { IconDeviceTablet, IconFileTypePdf, IconPaperclip, IconUpload, IconUserShare, IconX } from '@tabler/icons-react'
import { fileTypes, fileUrl, MAX_FILE_BYTES } from '@tnn/core'
import { type ReactNode, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { api, isApiError } from '../api/client'
import type { CustomFieldProps } from '../resource/config'
import { useFieldLabel } from '../resource/FieldInput'
import { useScreenGate } from '../screen/screen'
import { AskDialog, HandoffStatus, useHandoff } from '../tasks/handoff'
import classes from './file.module.css'

export interface FileUploadProps {
  label?: ReactNode
  description?: ReactNode
  /** The file's id (`POST /api/files`), or null */
  value: string | null
  onChange: (id: string | null) => void
  readOnly?: boolean
  withAsterisk?: boolean
  error?: ReactNode
  /** The record's field it is (ResourceForm), so a task asked for it goes on the saved record too */
  field?: string
  /** What a task is asked for, when `label` is not plain text (a list's own label) */
  taskLabel?: string
  /** Several files at once from another device (a list's next one): all of them; otherwise the first goes to `onChange` */
  onFiles?: (ids: string[]) => void
}

/**
 * One attached file: a slip photo taken with the phone camera, an image or a PDF. Shows a
 * thumbnail that opens the file; uploading happens on pick, so the form only keeps the id.
 * Signed in, attaching offers to do it on one's portable screen or to ask someone (`useHandoff`): the field waits, then
 * fills in.
 */
export function FileUpload({
  label,
  description,
  value,
  onChange,
  readOnly,
  withAsterisk,
  error,
  field,
  taskLabel,
  onFiles,
}: FileUploadProps) {
  const { t } = useTranslation()
  const labelling = useFieldLabel()
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string>()
  const [isFile, setIsFile] = useState(false)
  const [asking, setAsking] = useState(false)
  // On a touch screen the camera is at hand: one's screen is offered only once there is one
  const touch = useMediaQuery('(pointer: coarse)')
  const named = taskLabel ?? (typeof label === 'string' ? label : t('file.attach'))
  const handoff = useHandoff({
    kind: onFiles ? 'files' : 'file',
    label: named,
    field,
    onDone: (task) => (onFiles ? onFiles(task.fileIds) : onChange(task.fileIds[0] ?? null)),
  })
  const gate = useScreenGate(handoff.available)
  const pick = useRef<() => void>(undefined)
  const onMyScreen = () =>
    gate.run(async () => {
      setProblem(undefined)
      try {
        if (handoff.myId) await handoff.ask(handoff.myId)
      } catch (err) {
        setProblem(isApiError(err) ? err.message : t('task.failed'))
      }
    })

  const upload = async (file: File | null) => {
    if (!file) return
    setProblem(undefined)
    if (file.size > MAX_FILE_BYTES) return setProblem(t('file.tooBig'))
    setBusy(true)
    try {
      const stored = await api.upload(file)
      setIsFile(stored.type === 'application/pdf')
      onChange(stored.id)
    } catch (err) {
      setProblem(isApiError(err) && err.message !== 'network' ? err.message : t('file.failed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Input.Wrapper
      id={labelling.id}
      labelProps={labelling.labelProps}
      label={label}
      description={description}
      withAsterisk={withAsterisk}
      error={problem ?? error}
    >
      <div className={classes.box}>
        {value ? (
          <div className={classes.tile}>
            <Anchor href={fileUrl(value)} target="_blank" rel="noreferrer" className={classes.thumb} aria-label={t('file.view')}>
              {isFile ? <IconFileTypePdf size={28} stroke={1.5} /> : <img src={fileUrl(value)} alt="" onError={() => setIsFile(true)} />}
            </Anchor>
            <Anchor href={fileUrl(value)} target="_blank" rel="noreferrer" size="sm" aria-describedby={labelling['aria-labelledby']}>
              {t('file.view')}
            </Anchor>
            {!readOnly && (
              <Tooltip label={t('file.remove')}>
                <ActionIcon ml="auto" onClick={() => onChange(null)} aria-label={t('file.remove')}>
                  <IconX size={18} />
                </ActionIcon>
              </Tooltip>
            )}
          </div>
        ) : readOnly ? (
          <Text size="sm" c="dimmed" className={classes.empty}>
            –
          </Text>
        ) : handoff.task ? (
          <HandoffStatus handoff={handoff} />
        ) : handoff.available ? (
          <>
            <FileButton onChange={upload} accept={fileTypes.join(',')}>
              {({ onClick }) => {
                pick.current = onClick
                return null
              }}
            </FileButton>
            <Menu position="bottom-start" width="target">
              <Menu.Target>
                <Button
                  aria-describedby={labelling['aria-labelledby']}
                  variant="light"
                  leftSection={<IconPaperclip size={18} />}
                  loading={busy}
                  fullWidth
                >
                  {t('file.attach')}
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item leftSection={<IconUpload size={18} />} onClick={() => pick.current?.()}>
                  {touch ? t('task.takeOrPick') : t('task.thisDevice')}
                </Menu.Item>
                {gate.screen ? (
                  <Menu.Item
                    leftSection={<IconDeviceTablet size={18} />}
                    rightSection={
                      <Text size="xs" c="dimmed">
                        {gate.screen.name}
                      </Text>
                    }
                    onClick={onMyScreen}
                  >
                    {t('screen.attachThere')}
                  </Menu.Item>
                ) : (
                  !touch && (
                    <Menu.Item leftSection={<IconDeviceTablet size={18} />} onClick={onMyScreen}>
                      {t('screen.pairToAttach')}
                    </Menu.Item>
                  )
                )}
                <Menu.Item leftSection={<IconUserShare size={18} />} onClick={() => setAsking(true)}>
                  {t('task.askSomeone')}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
            <AskDialog opened={asking} onClose={() => setAsking(false)} label={named} handoff={handoff} />
            {gate.dialog}
          </>
        ) : (
          <FileButton onChange={upload} accept={fileTypes.join(',')}>
            {(props) => (
              <Button
                {...props}
                aria-describedby={labelling['aria-labelledby']}
                variant="light"
                leftSection={<IconPaperclip size={18} />}
                loading={busy}
                fullWidth
              >
                {t('file.attach')}
              </Button>
            )}
          </FileButton>
        )}
      </div>
    </Input.Wrapper>
  )
}

/** A small thumbnail of an attached file that opens it (slips in a list of payments) */
export function FileThumb({ id, label }: { id: string; label: string }) {
  const [isFile, setIsFile] = useState(false)
  return (
    <Anchor href={fileUrl(id)} target="_blank" rel="noreferrer" className={classes.thumb} aria-label={label}>
      {isFile ? <IconFileTypePdf size={24} stroke={1.5} /> : <img src={fileUrl(id)} alt="" onError={() => setIsFile(true)} />}
    </Anchor>
  )
}

/** Form field for a `file` field */
export function FileField({ form, readOnly, t, field }: CustomFieldProps) {
  const input = form.getInputProps(field.name)
  return (
    <FileUpload
      field={field.name}
      label={t(field.label)}
      withAsterisk={field.required && !readOnly}
      readOnly={readOnly}
      error={input.error}
      value={(input.value as string | null) || null}
      onChange={input.onChange}
    />
  )
}

export interface FilesUploadProps {
  label?: ReactNode
  description?: ReactNode
  /** Ids of the files attached (`POST /api/files`) */
  value: readonly string[]
  onChange: (ids: string[]) => void
  readOnly?: boolean
  withAsterisk?: boolean
  error?: ReactNode
  /** The record's field it is, for a task asked for it (`FileUpload`) */
  field?: string
}

/** Several attached files (an ID card and a contract, a medical certificate…): each one a file, and room to add another */
export function FilesUpload({ label, description, value, onChange, readOnly, withAsterisk, error, field }: FilesUploadProps) {
  const labelling = useFieldLabel()
  return (
    <Input.Wrapper {...labelling} label={label} description={description} withAsterisk={withAsterisk} error={error}>
      <Stack gap="sm" mt={4}>
        {value.map((id, i) => (
          <FileUpload
            key={id}
            value={id}
            readOnly={readOnly}
            onChange={(next) => onChange(next ? value.map((x, j) => (j === i ? next : x)) : value.filter((_, j) => j !== i))}
          />
        ))}
        {!readOnly && (
          <FileUpload
            value={null}
            field={field}
            taskLabel={typeof label === 'string' ? label : undefined}
            onChange={(id) => id && onChange([...value, id])}
            onFiles={(ids) => onChange([...value, ...ids.filter((id) => !value.includes(id))])}
          />
        )}
      </Stack>
    </Input.Wrapper>
  )
}

/** Form field for a list of files (a `json` field holding file ids, marked `form.custom`) */
export function FilesField({ form, readOnly, t, field, description }: CustomFieldProps & { description?: ReactNode }) {
  const input = form.getInputProps(field.name)
  return (
    <FilesUpload
      field={field.name}
      label={t(field.label)}
      description={readOnly ? undefined : description}
      withAsterisk={field.required && !readOnly}
      readOnly={readOnly}
      error={input.error}
      value={(form.values[field.name] as string[] | undefined) ?? []}
      onChange={(ids) => form.setFieldValue(field.name, ids)}
    />
  )
}
